import { useEffect, useState } from 'react';
import { ContentType, SlugPrefix } from '../models/pages';
import {
  defaultLocale, formatDateMedium, formatDateShort, formatDateYear,
} from './index';

const mapMediaCollectionItem = (item:any) => {
  switch (item.internal.type) {
    case (ContentType.ContentfulEpisode):
      return {
        ...item,
        slug: `${item.node_locale === defaultLocale ? '' : `/${item.node_locale}`}${SlugPrefix.ChdTvShows}${item.show[0]?.slug}/${item.slug}`,
        firstDescriptionField: `${formatDateShort(item.publishDate || item.createdAt)} - EPISODE ${item.episodeNumber ? item.episodeNumber : ''}`,
      };
    case (ContentType.ContentfulEventVideo):
      return {
        ...item,
        slug: `${item.node_locale === defaultLocale ? '' : `/${item.node_locale}`}${SlugPrefix.ChdTvEvents}${item.event[0]?.slug}/${item.slug}`,
        firstDescriptionField: formatDateShort(item.publishDate),
        formattedDate: formatDateMedium(item.publishDate || item.createdAt),
        secondDescriptionField: 'EVENT VIDEO',
      };
    case (ContentType.ContentfulShow):
      return {
        ...item,
        slug: `${item.node_locale === defaultLocale ? '' : `/${item.node_locale}`}${SlugPrefix.ChdTvShows}${item.slug}`,
        firstDescriptionField: formatDateYear(item.publishDate || item.createdAt),
        secondDescriptionField: 'SHOW',
        videoIds: item?.episodes?.map((e:any) => e.contentful_id) || [],
      };

    case (ContentType.ContentfulEvent):
      return {
        ...item,
        slug: `${item.node_locale === defaultLocale ? '' : `/${item.node_locale}`}${SlugPrefix.ChdTvEvents}${item.slug}`,
        firstDescriptionField: formatDateYear(item.publishDate || item.createdAt),
        formattedDate: formatDateMedium(item.publishDate || item.createdAt),
        secondDescriptionField: 'EVENT',
        videoIds: item?.eventVideos?.map((e:any) => e.contentful_id) || [],
      };

    case (ContentType.ContentfulVideo):
      return {
        ...item,
        slug: `${item.node_locale === defaultLocale ? '' : `/${item.node_locale}`}${SlugPrefix.ChdTvVideos}${item.slug}`,
        firstDescriptionField: formatDateShort(item.publishDate || item.createdAt),
        secondDescriptionField: 'VIDEO',
      };
    default:
      return null;
  }
};

const filterMediaCollection = (item:any) => {
  switch (item.internal.type) {
    case (ContentType.ContentfulEpisode):
      if (item.visible && item.show && item.show[0]?.visible && item.slug) {
        return true;
      }
      return false;
    case (ContentType.ContentfulEventVideo):
      if (item.visible && item.event && item.event[0]?.visible && item.slug) {
        return true;
      }
      return false;
    case (ContentType.ContentfulShow):
      if (item.visible && item.slug) {
        return true;
      }
      return false;
    case (ContentType.ContentfulEvent):
      if (item.visible && item.slug) {
        return true;
      }
      return false;
    case (ContentType.ContentfulVideo):
      if (item.visible && item.slug) {
        return true;
      }
      return false;
    default:
      return false;
  }
};

const sortAndFilterMediaCollectionVideos = (
  customMediaItems: any[],
  standardMediaItems: any[],
  pinItems: boolean,
  sortOrder: string,
) => {
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  const itemList: any[] = [];
  if (pinItems) {
    itemList.push(...standardMediaItems);
  } else {
    itemList.push(...standardMediaItems, ...customMediaItems);
  }

  const sortedItems = !hasMounted ? itemList : itemList.sort((a, b) => {
    if (sortOrder && sortOrder === 'Reverse Chronological') {
      if (a.publishDate && b.publishDate) {
        return a.publishDate > b.publishDate ? -1 : 1;
      } if (b.publishDate) {
        return 1;
      }
      return -1;
    }
    if (a.publishDate && b.publishDate) {
      return a.publishDate > b.publishDate ? 1 : -1;
    } if (b.publishDate) {
      return -1;
    }
    return 1;
  });

  if (pinItems) {
    sortedItems.unshift(...customMediaItems);
  }

  const filteredItems = sortedItems.reduce((acc: any[], item: any) => {
    if (!acc.find((accItem) => accItem.id === item.id)) {
      acc.push(item);
    }
    return acc;
  }, []);

  return filteredItems.filter((v) => filterMediaCollection(v));
};

const sortMediaCollectionVideos = (
  customMediaItems: any[],
  standardMediaItems: any[],
  pinItems: boolean,
  sortOrder: string,
) => {
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  const itemList: any[] = [];
  if (pinItems) {
    itemList.push(...standardMediaItems);
  } else {
    itemList.push(...standardMediaItems, ...customMediaItems);
  }
  const sortedItems = !hasMounted ? itemList : itemList.sort((a, b) => {
    if (sortOrder && sortOrder === 'Reverse Chronological') {
      if (a.publishDate && b.publishDate) {
        return a.publishDate > b.publishDate ? -1 : 1;
      } if (b.publishDate) {
        return 1;
      }
      return -1;
    }
    if (a.publishDate && b.publishDate) {
      return a.publishDate > b.publishDate ? 1 : -1;
    } if (b.publishDate) {
      return -1;
    }
    return 1;
  });

  if (pinItems) {
    sortedItems.unshift(...customMediaItems);
  }

  const filteredItems = sortedItems.reduce((acc: any[], item: any) => {
    /*
        The acc.find() is to eliminate duplicates. While this could
        have been done prior to sorting and filtering, this is the
        easiest and cleanest place to do it.
        */
    if (!acc.find((accItem) => accItem.id === item.id)) {
      acc.push(item);
    }
    return acc;
  }, []);
  return filteredItems.filter((v) => filterMediaCollection(v));
};

export {
  mapMediaCollectionItem,
  sortAndFilterMediaCollectionVideos,
  sortMediaCollectionVideos,
};
